import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '@Sage/hoi-poi-ui';
import { ExternalEmbedModal, getAllowedOriginFromSrc } from '@Sage/iframe-embed';

import { EntityCrudActions } from 'actions';
import { SALESORDERS } from 'constants/Entities';
import { IS_PRODUCTION } from 'constants/Environment';
import Context from 'managers/Context';
import { persistTokens, refreshToken, ExternalService } from 'services';
import { getActiveCrud } from 'utils/crud';
import {
    ensureAbsoluteHttpUrl,
    getExternalCrudFallbackUrl,
    normalizeExternalCrudConfig,
} from 'utils/externalCrud';
import { getLiteral } from 'utils/getLiteral';

const ENTITY_KEY = SALESORDERS.entity;
const IFRAME_TITLE = 's200uki Sales Orders Quotes';
const IFRAME_ALLOW = IS_PRODUCTION ? undefined : 'local-network-access';
const MAX_IFRAME_HEIGHT = 820;
const EMBED_TOAST_DEFAULTS = {
    autoClose: 6000,
    position: 'bottomRight',
    transition: 'slide',
    useDefaultCloseButton: true,
};

const getRuntimeAuthToken = (fallbackToken) => {
    return (
        Context?.config?.token || window.sessionStorage.getItem('fm_token') || fallbackToken || null
    );
};

const getRuntimeRefreshToken = () => {
    return Context?.config?.refreshToken || window.sessionStorage.getItem('refreshToken') || null;
};

const refreshRuntimeAuthToken = async (fallbackToken) => {
    const currentRefreshToken = getRuntimeRefreshToken();
    if (!currentRefreshToken) return getRuntimeAuthToken(fallbackToken);

    const tokens = await refreshToken(currentRefreshToken);
    persistTokens(tokens);
    return tokens?.token || getRuntimeAuthToken(fallbackToken);
};

const getEmbedEntityId = (value) => {
    if (value == null) return null;

    const parsedValue = Number.parseInt(value, 10);
    return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

const appendParentOrigin = (src) => {
    if (!src) return null;

    try {
        const url = new URL(src);
        url.searchParams.set('parentOrigin', window.location.origin);
        return url.toString();
    } catch {
        return src;
    }
};

const normalizeEmbedInitContext = (initialPayload) => {
    const apiBaseUrl = ensureAbsoluteHttpUrl(
        initialPayload?.apiBaseUrl || initialPayload?.api_base_url,
    );

    return {
        entityKey: ENTITY_KEY,
        ...(initialPayload || {}),
        ...(apiBaseUrl ? { apiBaseUrl } : {}),
    };
};

const SalesOrdersEmbeddedCrud = memo(
    ({ isOpen: isOpenProp, mode: modeProp, onClose: onCloseProp, title: titleProp }) => {
        // --- Context & hooks
        const dispatch = useDispatch();

        // --- State
        const { activeCrud, externalCrudConfigValue, isOpen, token } = useSelector((state) => {
            const currentActiveCrud = getActiveCrud(state);

            return {
                activeCrud: currentActiveCrud,
                externalCrudConfigValue:
                    state.config?.userData?.entitiesWithExternalCRUD?.[ENTITY_KEY],
                isOpen: state.entityCrud?.isOpen && currentActiveCrud?.entity === ENTITY_KEY,
                token: state.config?.token,
            };
        });
        const [embedInitContext, setEmbedInitContext] = useState(null);

        // --- Derived state
        const mode = modeProp || (activeCrud?.id ? 'edit' : 'create');
        const modalIsOpen = isOpenProp ?? isOpen;
        const entityId = getEmbedEntityId(activeCrud?.id);
        const externalCrudConfig = useMemo(() => {
            return normalizeExternalCrudConfig(externalCrudConfigValue);
        }, [externalCrudConfigValue]);
        const normalizedIframeSrc = ensureAbsoluteHttpUrl(
            getExternalCrudFallbackUrl(ENTITY_KEY) || embedInitContext?.url,
        );
        const iframeSrc = appendParentOrigin(normalizedIframeSrc);
        const title =
            titleProp ||
            (mode === 'edit'
                ? getLiteral('label_editing_salesorders')
                : getLiteral('title_add_salesorder'));

        // --- Handlers
        const getAuthContext = useCallback(
            async ({ request } = {}) => {
                const nextToken =
                    request?.reason === 'unauthorized'
                        ? await refreshRuntimeAuthToken(token)
                        : getRuntimeAuthToken(token);

                return {
                    token: nextToken,
                };
            },
            [token],
        );

        const onClose = useCallback(() => {
            if (onCloseProp) {
                onCloseProp();
                return;
            }

            dispatch(EntityCrudActions.close(false));
        }, [dispatch, onCloseProp]);

        const onEmbedNotify = useCallback((payload = {}) => {
            const message = payload?.text ?? payload?.message;
            if (!message) return;

            showToast({
                ...EMBED_TOAST_DEFAULTS,
                text: message,
                title: payload?.title,
                type: payload?.type ?? 'info',
            });
        }, []);

        // --- Effects
        useEffect(() => {
            if (!modalIsOpen || !externalCrudConfig) {
                setEmbedInitContext(null);
                return undefined;
            }

            let isActive = true;
            setEmbedInitContext(null);

            const loadEmbedInitContext = async () => {
                try {
                    const initialPayload = await ExternalService.initIframe({
                        ...externalCrudConfig,
                        entityType: ENTITY_KEY,
                        ...(entityId !== null ? { entityId } : {}),
                    });

                    if (isActive) {
                        setEmbedInitContext(normalizeEmbedInitContext(initialPayload));
                    }
                } catch (error) {
                    if (isActive) {
                        setEmbedInitContext(null);
                    }
                }
            };

            void loadEmbedInitContext();

            return () => {
                isActive = false;
            };
        }, [entityId, externalCrudConfig, modalIsOpen]);

        // --- Derived (for render)
        const shouldRenderModal = modalIsOpen && !!externalCrudConfig && !!embedInitContext;

        // --- Memoized props
        const modalProps = useMemo(
            () => ({
                allow: IFRAME_ALLOW,
                allowedOrigin: getAllowedOriginFromSrc(iframeSrc),
                getAuthContext,
                iframeSrc,
                iframeSizing: 'fill',
                iframeTitle: IFRAME_TITLE,
                initContext: embedInitContext,
                initExtras: { supportsNotifications: false },
                isOpen: shouldRenderModal,
                loadErrorText: getLiteral('error_connection'),
                maxIframeHeight: MAX_IFRAME_HEIGHT,
                mode,
                onClose,
                onEmbedNotify,
                renderStateFooter: false,
                size: 'huge',
                title,
            }),
            [
                embedInitContext,
                getAuthContext,
                iframeSrc,
                mode,
                onClose,
                onEmbedNotify,
                shouldRenderModal,
                title,
            ],
        );

        // --- Render
        return <ExternalEmbedModal {...modalProps} />;
    },
);

SalesOrdersEmbeddedCrud.displayName = 'SalesOrdersEmbeddedCrud';

export default SalesOrdersEmbeddedCrud;
